import React, { useState } from "react"
import tw, { css, styled } from "twin.macro"
import { useTranslation } from "next-i18next"
import { Button, ButtonTheme } from "../formElements/button"
import { useRouter } from "next/router"
import { Routes } from "../../routes"
import { ArtistOnboardingModal } from "../artistOnboardingModal"
import { Modal } from "../modal"
import { ShareContent } from "../shareProfile"
import {
  AuthRoleType,
  GuildModerator,
  ProfileOnGetProfileFragment,
  useGetMyGuildModerationStatusLazyQuery,
  UserOnGetUserFragmentFragment,
  useValidateFacebookAccessTokenMutation,
} from "../../graphql/generated/graphql"
import { ReactComponent as YouTubeIcon } from "../../assets/profile/youTube-black.svg"
import { ReactComponent as DiscordIcon } from "../../assets/profile/discord-black.svg"
import { ReactComponent as CheckedIcon } from "../../assets/alert/check-circle-green.svg"
import { ReactComponent as AlertIcon } from "../../assets/alert/alert.svg"
import { ReactComponent as InstagramIcon } from "../../assets/profile/instagram-black.svg"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { InstagramIntegration } from "./instagramIntegration"
import { YouTubeIntegration } from "./youTubeIntegration"
import { DiscordIntegration } from "./discordIntegration"

interface StyleProp {
  show: boolean
}
const Caret = styled.span(({ show }: StyleProp) => [
  tw`absolute right-0 inline-block w-3 h-3 order-2 border-[#BC3EF7] cursor-pointer`,
  css`
    content: "";
    border-width: 0 2px 2px 0;
    transform: ${show ? "rotate(-135deg)" : "rotate(45deg)"};
  `,
  show ? tw`mt-1` : tw`mb-1`,
])

interface UserSidebarProps {
  user: UserOnGetUserFragmentFragment
  profile: ProfileOnGetProfileFragment
  refetchProfileData: () => Promise<Record<string, any>>
}
export const UserSidebar = ({ user, profile, refetchProfileData }: UserSidebarProps) => {
  const { email, isInstagramLinked, youTubeAccountDetails, authRole } = user
  const { price: questionPrice, isTakingQuestions } = profile
  const [price, setPrice] = useState(!!questionPrice ? `${questionPrice}` : "0")
  const [show, setShow] = useState(false)
  const [validFacebookAccessToken, setValidFacebookAccessToken] = useState(false)
  const [showIntegrations, setShowIntegrations] = useState(false)
  const { t } = useTranslation("profile")
  const router = useRouter()
  const [getDiscordIntegrationDetails, { loading: discordDataLoading }] = useGetMyGuildModerationStatusLazyQuery()
  const [validateFacebookAccessToken] = useValidateFacebookAccessTokenMutation()
  const [discordGuildModData, setDiscordGuildModData] = useState<GuildModerator[] | null>(null)
  const staffUser = authRole === AuthRoleType.Staff

  const renderIcon = () => {
    switch (true) {
      case validFacebookAccessToken && isInstagramLinked:
        return <CheckedIcon tw="absolute -right-2 bottom-0" />
      default:
        return <AlertIcon tw="w-[16px] h-[16px] absolute -right-2 bottom-0" />
    }
  }

  const renderYouTubeStatusIcon = () => {
    return youTubeAccountDetails?.isLinked ? (
      <CheckedIcon tw="absolute -right-2 bottom-0" />
    ) : (
      <AlertIcon tw="w-[16px] h-[16px] absolute -right-2 bottom-0" />
    )
  }

  useEffectAsync(async () => {
    const { data } = await validateFacebookAccessToken()
    setValidFacebookAccessToken(data?.ValidateFacebookAccessToken as boolean)

    const { data: discordModData } = await getDiscordIntegrationDetails()
    discordModData && setDiscordGuildModData(discordModData.GuildModerators)
  }, [])

  if (!isTakingQuestions) {
    const artistOnboardingModalProps = {
      email,
      profile,
      price,
      setPrice,
      refetchProfileData,
      isFullWidth: false,
    }
    return <ArtistOnboardingModal {...artistOnboardingModalProps} />
  }
  return (
    <div tw="my-8 tablet-lg:my-0">
      <div
        data-testid="user-sidebar"
        tw="bg-white flex flex-col w-full tablet-lg:(w-80) rounded-lg shadow-base-2 text-center px-6 py-8 space-y-3"
      >
        <h3 tw="text-lg">{t("user_sidebar.heading")}</h3>
        <span tw="text-md font-bold">{t("ask_question.myprice", { price })}</span>
        <Button
          dataTestId={"button-share-profile"}
          onClick={() => setShow(true)}
          action={t("user_sidebar.share_profile")}
          twStyle={tw`w-full`}
        />
        <Button
          dataTestId={"button-edit-profile"}
          theme={ButtonTheme.PrimaryOutline}
          onClick={async () => await router.push(Routes.EditPrice)}
          action={t("user_sidebar.edit_price")}
          twStyle={tw`w-full`}
        />
        <Modal {...{ show, setShow, component: <ShareContent {...{ profile, isActive: true }} /> }} />
      </div>
      {staffUser && (
        <div tw="bg-white flex flex-col w-full tablet-lg:(w-80) rounded-lg shadow-base-2 mt-4 p-3 space-y-3">
          <div tw="relative flex items-center space-x-3">
            <h2 tw="text-[20px] font-bold">{t("Integrations.title")}</h2>
            <span tw="relative">
              <DiscordIcon tw="w-[24px] h-[24px]" />
              <AlertIcon tw="w-[16px] h-[16px] absolute -right-2 bottom-0" />
            </span>
            <span tw="relative">
              <YouTubeIcon tw="w-[24px] h-[24px]" />
              {renderYouTubeStatusIcon()}
            </span>
            <span tw="relative">
              <InstagramIcon tw="w-[24px] h-[24px]" />
              {renderIcon()}
            </span>
            <Caret show={showIntegrations} onClick={() => setShowIntegrations(!showIntegrations)} />
          </div>
          <p tw="text-[14px]">{t("Integrations.manage")}</p>

          {!discordDataLoading && discordGuildModData ? (
            <DiscordIntegration {...{ showIntegrations, guildModeratorData: discordGuildModData }} />
          ) : (
            <></>
          )}
          <YouTubeIntegration {...{ showIntegrations, youTubeAccountDetails }} />
          <InstagramIntegration {...{ showIntegrations, validFacebookAccessToken, isInstagramLinked }} />
        </div>
      )}
    </div>
  )
}
